import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../lib/errorTracking';

const VT = "'VT323', monospace";
const ACCENT = '#86d46f';

interface Props {
  children: ReactNode;
}

interface State {
  crashed: boolean;
}

/**
 * Last line of defence for a render crash.
 *
 * Without this, a throw anywhere in the tree unmounts everything and the
 * founder gets a white page: no explanation, no way back, and no reason to
 * believe their work survived. It did — the server owns the state — so say so.
 *
 * A class component because React offers no hook equivalent of
 * componentDidCatch.
 */
export default class CrashScreen extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console first: reportError is inert without a DSN, and in dev the stack
    // in the console is the only trace of what happened.
    console.error('[FOUNDR-XI] render crash:', error, info.componentStack);
    reportError(error);
  }

  render(): ReactNode {
    if (!this.state.crashed) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          background: '#070b09',
          color: '#c4d2c8',
          fontFamily: VT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <p style={{ color: ACCENT, fontSize: 26, margin: 0, letterSpacing: '0.06em' }}>
          ▸ BUNKER LIGHTS OUT
        </p>
        <p style={{ fontSize: 19, margin: 0, maxWidth: '44ch', lineHeight: 1.4 }}>
          Something in the interface broke. Nothing has been lost — your vault lives on the
          server, not in this tab.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: ACCENT,
            border: `1px solid ${ACCENT}`,
            color: '#06110a',
            fontFamily: VT,
            fontSize: 19,
            letterSpacing: '0.06em',
            padding: '7px 18px',
            cursor: 'pointer',
          }}
        >
          RELOAD THE BUNKER ▸
        </button>
        <p style={{ fontSize: 16, margin: 0, opacity: 0.6, maxWidth: '44ch' }}>
          It has been reported. If it keeps happening, the feedback button is the fastest way
          to reach Nicolas.
        </p>
      </div>
    );
  }
}
